import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  ArrowDownToLine,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Link2,
  Loader2,
  History,
  PackageOpen,
  PencilLine,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { supabase } from "@/lib/supabaseClient";

const EMPTY_SNAPSHOT = { supplies: [], procedures: [], category_rules: [], visit_kit: [] };
const INITIAL_SUPPLY_FORM = {
  name: "",
  sku: "",
  category: "Clinic supplies",
  stockUnit: "piece",
  openingQuantity: "0",
  reorderPoint: "0",
  countingMethod: "exact",
  usesPerStockUnit: "20",
  procedureIds: [],
  unitsPerUse: "1",
  usageBasis: "treatment",
  usageScope: "completed_treatment",
};
const STARTER_SUPPLIES = [
  { label: "Gloves", name: "Nitrile gloves", category: "Patient visit supplies", stockUnit: "box", countingMethod: "estimate", usesPerStockUnit: "25", usageBasis: "patient_visit" },
  { label: "Masks", name: "Surgical masks", category: "Patient visit supplies", stockUnit: "box", countingMethod: "estimate", usesPerStockUnit: "50", usageBasis: "patient_visit" },
  { label: "Composite capsule", name: "Composite resin capsule", category: "Restorative", stockUnit: "capsule", countingMethod: "exact" },
  { label: "Anesthetic cartridge", name: "Local anesthetic cartridge", category: "Anesthesia", stockUnit: "cartridge", countingMethod: "exact" },
  { label: "Prophy paste", name: "Prophy paste", category: "Preventive", stockUnit: "tube", countingMethod: "estimate", usesPerStockUnit: "20" },
];
const STOCK_UNIT_OPTIONS = [
  { value: "piece", label: "Piece" },
  { value: "capsule", label: "Capsule" },
  { value: "cartridge", label: "Cartridge" },
  { value: "tube", label: "Tube" },
  { value: "bottle", label: "Bottle" },
  { value: "box", label: "Box" },
  { value: "pack", label: "Pack" },
  { value: "pouch", label: "Pouch" },
  { value: "roll", label: "Roll" },
  { value: "pair", label: "Pair" },
  { value: "set", label: "Set" },
  { value: "kit", label: "Kit" },
  { value: "syringe", label: "Syringe" },
  { value: "vial", label: "Vial" },
];
const STATUS_STYLES = {
  out: { label: "Out of stock", classes: "border-rose-200 bg-rose-50 text-rose-800" },
  low: { label: "Low stock", classes: "border-amber-200 bg-amber-50 text-amber-800" },
  in_stock: { label: "In stock", classes: "border-emerald-200 bg-emerald-50 text-emerald-800" },
};

function newRequestId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  return `inventory-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function formatQuantity(value) {
  const numeric = numberValue(value);
  return new Intl.NumberFormat("en-PH", { maximumFractionDigits: 2 }).format(numeric);
}

function quantityUnit(unit, quantity) {
  const clean = String(unit || "unit").trim();
  if (Math.abs(numberValue(quantity)) === 1 || clean.endsWith("s")) return clean;
  if (/(s|x|z|ch|sh)$/i.test(clean)) return `${clean}es`;
  if (/[^aeiou]y$/i.test(clean)) return `${clean.slice(0, -1)}ies`;
  return `${clean}s`;
}

function stockUnitName(stockUnit) {
  return String(stockUnit || "unit").trim().toLowerCase() || "unit";
}

function countingChoicesFor(stockUnit) {
  const unit = stockUnitName(stockUnit);
  const units = quantityUnit(unit, 2);
  return [
    {
      id: "exact",
      label: `Use a set number of ${units} each time`,
      help: `Choose this when a patient visit or treatment normally uses 1, 10, or another set number of ${units}.`,
    },
    {
      id: "estimate",
      label: `One ${unit} lasts for several uses`,
      help: `Choose this when only part of a ${unit} is used each time.`,
    },
    {
      id: "manual",
      label: `Count the ${units} left on the shelf`,
      help: `Choose this when the number of ${units} used changes and staff count what remains.`,
    },
  ];
}

function formatDate(value) {
  if (!value) return "Not counted yet";
  return new Intl.DateTimeFormat("en-PH", { month: "short", day: "numeric", year: "numeric" }).format(new Date(value));
}

function clinicDateKey(value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: "Asia/Manila",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const part = (type) => parts.find((entry) => entry.type === type)?.value || "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function formatReportDate(value) {
  if (!value) return "Selected day";
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(new Date(`${value}T12:00:00+08:00`));
}

function formatMovementTime(value) {
  if (!value) return "";
  return new Intl.DateTimeFormat("en-PH", {
    timeZone: "Asia/Manila",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function movementLabel(kind) {
  return {
    opening_balance: "Opening stock",
    receipt: "Stock received",
    treatment_usage: "Treatment use",
    visit_usage: "Patient visit kit",
    stock_count: "Shelf count",
    adjustment: "Adjustment",
    reversal: "Correction",
  }[kind] || "Stock change";
}

function usageSentence(material) {
  if (material.counting_method === "estimate") {
    return `1 ${material.stock_unit} lasts about ${formatQuantity(material.uses_per_stock_unit)} treatments`;
  }
  const timing = material.usage_scope === "patient_visit" ? "after every appointment" : "when the treatment is completed";
  return `${formatQuantity(material.units_per_use)} ${quantityUnit(material.stock_unit, material.units_per_use)} ${timing}`;
}

function errorMessage(error, fallback) {
  return error?.message || fallback;
}

function exportInventoryCsv(supplies, branchName) {
  const headers = ["Supply", "SKU", "Category", "On hand", "Unit", "Reorder at", "Status", "30-day use"];
  const rows = supplies.map((item) => [
    item.name,
    item.sku || "",
    item.category,
    item.on_hand,
    item.stock_unit,
    item.reorder_point,
    STATUS_STYLES[item.status]?.label || item.status,
    item.used_30_days,
  ]);
  const escape = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((row) => row.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `${String(branchName || "branch").toLowerCase().replace(/[^a-z0-9]+/g, "-")}-inventory.csv`;
  anchor.click();
  URL.revokeObjectURL(url);
}

function PageState({ icon: Icon, title, body, action }) {
  return (
    <div className="mx-auto mt-16 max-w-lg rounded-xl border border-slate-200 bg-white px-6 py-12 text-center shadow-sm">
      <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-600">
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>
      <h1 className="mt-4 text-xl font-bold text-slate-950">{title}</h1>
      <p className="mt-2 text-sm leading-6 text-slate-600">{body}</p>
      {action}
    </div>
  );
}

function GuidedSupplySetup({ branchId, branchName, procedures, onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [saved, setSaved] = useState(null);
  const [procedureSearch, setProcedureSearch] = useState("");
  const [form, setForm] = useState(INITIAL_SUPPLY_FORM);
  const selectedProcedures = procedures.filter((procedure) => form.procedureIds.includes(procedure.procedure_id));
  const visibleProcedures = procedures.filter((procedure) => {
    const query = procedureSearch.trim().toLowerCase();
    if (!query) return true;
    return [procedure.name, procedure.code, procedure.category].some((value) => String(value || "").toLowerCase().includes(query));
  });
  const allProceduresSelected = procedures.length > 0
    && procedures.every((procedure) => form.procedureIds.includes(procedure.procedure_id));
  const someProceduresSelected = !allProceduresSelected
    && procedures.some((procedure) => form.procedureIds.includes(procedure.procedure_id));

  function update(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setFormError("");
  }

  function toggleProcedure(procedureId) {
    setForm((current) => ({
      ...current,
      procedureIds: current.procedureIds.includes(procedureId)
        ? current.procedureIds.filter((id) => id !== procedureId)
        : [...current.procedureIds, procedureId],
    }));
    setFormError("");
  }

  function toggleAllProcedures() {
    setForm((current) => ({
      ...current,
      procedureIds: allProceduresSelected
        ? []
        : procedures.map((procedure) => procedure.procedure_id),
    }));
    setFormError("");
  }

  function validateCurrentStep() {
    if (step === 1 && (!form.name.trim() || !form.category.trim() || !form.stockUnit.trim())) {
      return "Supply name, category, and stock unit are required.";
    }
    if (step === 1 && (numberValue(form.openingQuantity, -1) < 0 || numberValue(form.reorderPoint, -1) < 0)) {
      return "Opening stock and reorder point must be zero or more.";
    }
    if (step === 3 && form.countingMethod !== "manual" && form.usageBasis === "treatment" && form.procedureIds.length === 0) {
      return "Choose at least one treatment that uses this supply.";
    }
    if (step === 3 && form.countingMethod === "estimate" && numberValue(form.usesPerStockUnit) <= 0) {
      return `Enter how many treatments one ${stockUnitName(form.stockUnit)} normally lasts.`;
    }
    if (step === 3 && form.countingMethod === "exact" && numberValue(form.unitsPerUse) <= 0) {
      return `Enter how many ${quantityUnit(form.stockUnit, 2)} are normally used.`;
    }
    return "";
  }

  async function continueFlow() {
    const issue = validateCurrentStep();
    if (issue) {
      setFormError(issue);
      return;
    }
    if (step < 3) {
      setStep((current) => current + 1);
      return;
    }

    setSaving(true);
    setFormError("");
    const { data, error } = await supabase.rpc("inventory_admin_add_supply_scoped", {
      p_branch_id: branchId,
      p_name: form.name.trim(),
      p_sku: form.sku.trim() || null,
      p_category: form.category.trim(),
      p_stock_unit: form.stockUnit.trim(),
      p_counting_method: form.countingMethod,
      p_uses_per_stock_unit: form.countingMethod === "estimate" ? numberValue(form.usesPerStockUnit) : 1,
      p_opening_quantity: numberValue(form.openingQuantity),
      p_reorder_point: numberValue(form.reorderPoint),
      p_usage_basis: form.countingMethod === "manual" ? "manual" : form.usageBasis,
      p_procedure_ids: form.countingMethod === "manual" || form.usageBasis === "patient_visit" ? [] : form.procedureIds,
      p_units_per_use: form.countingMethod === "exact" ? numberValue(form.unitsPerUse, 1) : 1,
      p_usage_scope: form.countingMethod === "manual" ? "completed_treatment" : form.usageScope,
    });
    setSaving(false);
    if (error) {
      setFormError(errorMessage(error, "Could not add the supply."));
      return;
    }
    const deduction = form.countingMethod === "estimate"
      ? 1 / numberValue(form.usesPerStockUnit, 1)
      : numberValue(form.unitsPerUse, 1);
    setSaved({
      name: form.name.trim(),
      stockUnit: form.stockUnit.trim(),
      openingQuantity: numberValue(form.openingQuantity),
      countingMethod: form.countingMethod,
      procedureNames: selectedProcedures.map((procedure) => procedure.name),
      usageBasis: form.countingMethod === "manual" ? "manual" : form.usageBasis,
      usageScope: form.usageScope,
      deduction,
    });
    onSaved(data);
    toast.success(`${form.name.trim()} added to ${branchName}`);
  }

  function startAnother() {
    setForm(INITIAL_SUPPLY_FORM);
    setProcedureSearch("");
    setStep(1);
    setSaved(null);
    setFormError("");
  }

  function applyStarter(starter) {
    setForm((current) => ({
      ...current,
      name: starter.name,
      category: starter.category,
      stockUnit: starter.stockUnit,
      countingMethod: starter.countingMethod,
      usesPerStockUnit: starter.usesPerStockUnit || current.usesPerStockUnit,
      usageBasis: starter.usageBasis || current.usageBasis,
    }));
    setFormError("");
  }

  if (saved) {
    const afterNext = saved.openingQuantity - saved.deduction;
    const linkedTreatmentText = saved.procedureNames.length === 1
      ? saved.procedureNames[0]
      : `any of the ${saved.procedureNames.length} linked treatments`;
    return (
      <section className="overflow-hidden rounded-xl border border-emerald-200 bg-white shadow-sm" data-testid="supply-setup-success">
        <div className="mx-auto max-w-2xl px-6 py-12 text-center sm:py-16">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
            <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
          </span>
          <p className="mt-5 text-xs font-bold uppercase tracking-[0.08em] text-emerald-700">{saved.countingMethod === "manual" ? "Supply added" : "Tracking is on"}</p>
          <h2 className="mt-2 text-2xl font-bold tracking-[-0.02em] text-slate-950">{saved.name} {saved.countingMethod === "manual" ? "is on your stock list" : "is now tracked"}</h2>
          {saved.countingMethod === "manual" ? (
            <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-slate-600">Dental OS will keep the quantity you entered. Update it whenever your team completes a shelf count.</p>
          ) : (
            <>
              <p className="mx-auto mt-3 max-w-lg text-sm leading-6 text-slate-600">{saved.usageBasis === "patient_visit" ? `Dental OS records this supply once whenever a patient visit is completed at ${branchName}.` : saved.usageScope === "patient_visit" ? `Dental OS records the material used after every appointment for ${linkedTreatmentText}.` : `Dental OS records the material used when ${linkedTreatmentText} is completed.`}</p>
              <div className="mx-auto mt-6 grid max-w-md grid-cols-[1fr_auto_1fr] items-center gap-4 rounded-xl border border-slate-200 bg-slate-50 px-5 py-4">
                <div><p className="text-xs font-semibold text-slate-500">On hand now</p><p className="mt-1 text-2xl font-bold tabular-nums text-slate-950">{formatQuantity(saved.openingQuantity)}</p></div>
                <span className="text-lg text-slate-400" aria-hidden="true">→</span>
                <div><p className="text-xs font-semibold text-slate-500">{saved.usageBasis === "patient_visit" ? "After next patient visit" : saved.usageScope === "patient_visit" ? "After next appointment" : "After next treatment"}</p><p className="mt-1 text-2xl font-bold tabular-nums text-emerald-800">{formatQuantity(afterNext)}</p></div>
                <p className="col-span-3 text-xs text-slate-500">{saved.stockUnit}</p>
              </div>
            </>
          )}
          <div className="mt-8 flex flex-col justify-center gap-2 sm:flex-row sm:flex-wrap">
            <button type="button" onClick={onClose} className="min-h-11 w-full rounded-lg bg-slate-950 px-5 text-sm font-semibold text-white hover:bg-slate-800 sm:w-auto">Go to inventory</button>
            <button type="button" onClick={startAnother} className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-5 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto">Add another supply</button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="guided-supply-setup">
      <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-4 py-4 sm:gap-4 sm:px-5">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.08em] text-sky-700">Start inventory tracking</p>
          <h2 className="mt-1 text-xl font-bold text-slate-950">Track one supply first</h2>
          <p className="mt-1 text-sm text-slate-500">You can finish this in about five minutes.</p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close supply setup" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-slate-500 hover:bg-slate-100">
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>

      <div className="grid grid-cols-3 gap-2 border-b border-slate-200 bg-slate-50 px-3 py-3 sm:px-4">
        {["Supply", "Counting", "Usage"].map((label, index) => (
          <div key={label} className="flex flex-col items-center gap-1.5 text-center text-[11px] font-semibold sm:flex-row sm:text-left sm:text-xs">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ${step === index + 1 ? "bg-slate-950 text-white" : step > index + 1 ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-500"}`}>
              {step > index + 1 ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : index + 1}
            </span>
            <span className={step === index + 1 ? "text-slate-950" : "text-slate-500"}>{label}</span>
          </div>
        ))}
      </div>

      <div className="mx-auto min-h-[350px] max-w-2xl px-4 py-6 sm:px-5 sm:py-7">
        {step === 1 && (
          <div className="space-y-5">
            <div>
              <h3 className="text-xl font-bold text-slate-950">What supply do you want to track?</h3>
              <p className="mt-1 text-sm text-slate-500">Enter any item your clinic keeps in stock.</p>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Supply name" className="sm:col-span-2"><input data-testid="supply-name" autoFocus value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="e.g. Composite resin, gloves, toothpaste" className="field-input" /></Field>
              <div className="sm:col-span-2" data-testid="supply-example-shortcuts">
                <p className="text-xs font-semibold text-slate-600">Quick-fill examples (optional)</p>
                <p className="mt-1 text-xs text-slate-500">Choose one to fill the form, or enter your own supply above.</p>
                <div className="mt-2 flex flex-wrap gap-2" aria-label="Optional common supply examples">
                  {STARTER_SUPPLIES.map((starter) => {
                    const selected = form.name === starter.name;
                    return (
                      <button key={starter.label} type="button" aria-pressed={selected} onClick={() => applyStarter(starter)} className={`min-h-10 rounded-full border px-3.5 text-sm font-semibold transition-colors ${selected ? "border-slate-400 bg-slate-100 text-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"}`}>{starter.label}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Counted by</p>
                <StockUnitControl value={form.stockUnit} onChange={(value) => update("stockUnit", value)} testId="supply-stock-unit" customTestId="supply-custom-stock-unit" />
              </div>
              <Field label="How many are on the shelf now?"><input data-testid="supply-opening-quantity" type="number" min="0" step="0.01" value={form.openingQuantity} onChange={(event) => update("openingQuantity", event.target.value)} className="field-input text-right" /></Field>
              <Field label="Mark as low stock at"><input data-testid="supply-reorder-point" type="number" min="0" step="0.01" value={form.reorderPoint} onChange={(event) => update("reorderPoint", event.target.value)} className="field-input text-right" /></Field>
              <details className="sm:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                <summary className="cursor-pointer text-sm font-semibold text-slate-700">More details</summary>
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <Field label="SKU (optional)"><input data-testid="supply-sku" value={form.sku} onChange={(event) => update("sku", event.target.value)} placeholder="REST-COMP-A2" className="field-input bg-white" /></Field>
                  <Field label="Category"><input data-testid="supply-category" value={form.category} onChange={(event) => update("category", event.target.value)} placeholder="Restorative" className="field-input bg-white" /></Field>
                </div>
              </details>
            </div>
          </div>
        )}

        {step === 2 && (
          <div>
            <h3 className="text-xl font-bold text-slate-950">How do you normally use this supply?</h3>
            <p className="mt-1 text-sm text-slate-500">Choose the option that best matches what happens in your clinic.</p>
            <div className="mt-6 space-y-2">
              {countingChoicesFor(form.stockUnit).map((choice) => (
                <label key={choice.id} className={`flex cursor-pointer gap-3 rounded-lg border p-4 ${form.countingMethod === choice.id ? "border-slate-950 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <input type="radio" name="counting-method" checked={form.countingMethod === choice.id} onChange={() => update("countingMethod", choice.id)} className="mt-1 h-4 w-4" />
                  <span><span className="block text-sm font-bold text-slate-950">{choice.label}</span><span className="mt-0.5 block text-xs leading-5 text-slate-500">{choice.help}</span></span>
                </label>
              ))}
            </div>
          </div>
        )}

        {step === 3 && form.countingMethod === "manual" && (
          <div>
            <h3 className="text-xl font-bold text-slate-950">Confirm the stocktake method</h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">Dental OS will keep this supply on the stock sheet. Your team updates the number after checking the shelf.</p>
            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
              <p className="text-sm font-bold text-slate-950">{form.name}</p>
              <p className="mt-2 text-sm text-slate-700">Starting stock: {formatQuantity(form.openingQuantity)} {form.stockUnit}</p>
              <p className="mt-1 text-xs text-slate-500">Low-stock alert at {formatQuantity(form.reorderPoint)}</p>
            </div>
          </div>
        )}

        {step === 3 && form.countingMethod !== "manual" && (
          <div>
            <h3 className="text-xl font-bold text-slate-950">When is this supply normally used?</h3>
            <p className="mt-1 text-sm text-slate-500">Choose one clear rule. Dental OS will record the stock only after the visit is saved.</p>
            <div className="mt-5 grid gap-2 sm:grid-cols-2" role="radiogroup" aria-label="Supply usage rule">
              <label className={`flex cursor-pointer gap-3 rounded-lg border p-4 ${form.usageBasis === "patient_visit" ? "border-slate-950 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <input data-testid="supply-usage-patient-visit" type="radio" name="usage-basis" checked={form.usageBasis === "patient_visit"} onChange={() => update("usageBasis", "patient_visit")} className="mt-1 h-4 w-4" />
                <span><span className="block text-sm font-bold text-slate-950">Every patient visit</span><span className="mt-0.5 block text-xs leading-5 text-slate-500">For gloves, masks, bibs, cups, and other standard chair supplies.</span></span>
              </label>
              <label className={`flex cursor-pointer gap-3 rounded-lg border p-4 ${form.usageBasis === "treatment" ? "border-slate-950 bg-slate-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                <input data-testid="supply-usage-treatment" type="radio" name="usage-basis" checked={form.usageBasis === "treatment"} onChange={() => update("usageBasis", "treatment")} className="mt-1 h-4 w-4" />
                <span><span className="block text-sm font-bold text-slate-950">Only for specific treatments</span><span className="mt-0.5 block text-xs leading-5 text-slate-500">For anesthetic, composite, brackets, sutures, and treatment-specific materials.</span></span>
              </label>
            </div>
            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              {form.countingMethod === "estimate" && (
                <Field label={`One ${stockUnitName(form.stockUnit)} usually covers how many ${form.usageBasis === "patient_visit" ? "patient visits" : "treatments"}?`} className="sm:col-span-2">
                  <input data-testid="supply-uses-per-stock-unit" type="number" min="0.01" step="0.01" value={form.usesPerStockUnit} onChange={(event) => update("usesPerStockUnit", event.target.value)} className="field-input max-w-40 text-right" />
                </Field>
              )}
              {form.usageBasis === "treatment" ? <div className="sm:col-span-2">
                <p className="text-sm font-semibold text-slate-700">Which treatments use {form.name || "this supply"}?</p>
                <p className="mt-1 text-xs leading-5 text-slate-500">Select every treatment that normally uses it.</p>
                <label htmlFor="supply-treatment-search" className="text-sm font-semibold text-slate-700">Find treatments</label>
                <div className="relative mt-2">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
                  <input id="supply-treatment-search" data-testid="supply-treatment-search" type="search" value={procedureSearch} onChange={(event) => setProcedureSearch(event.target.value)} placeholder="Search treatment, category, or code" className="field-input pl-9" />
                </div>
                <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-slate-200 bg-white" role="group" aria-label="Treatments that use this supply" data-testid="supply-procedure-list">
                  {procedures.length > 0 && (
                    <label className={`sticky top-0 z-10 flex min-h-14 cursor-pointer items-start gap-3 border-b border-slate-200 px-4 py-3 ${allProceduresSelected ? "bg-sky-50" : "bg-white hover:bg-slate-50"}`}>
                      <input
                        ref={(checkbox) => {
                          if (checkbox) checkbox.indeterminate = someProceduresSelected;
                        }}
                        data-testid="supply-procedure-select-all"
                        type="checkbox"
                        checked={allProceduresSelected}
                        onChange={toggleAllProcedures}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-semibold text-slate-900">Select all treatments</span>
                        <span className="mt-0.5 block text-xs text-slate-500">All {procedures.length} treatments in this clinic</span>
                      </span>
                    </label>
                  )}
                  {visibleProcedures.length === 0 ? (
                    <p className="px-4 py-5 text-sm text-slate-500">No matching treatments found.</p>
                  ) : visibleProcedures.map((procedure) => {
                    const checked = form.procedureIds.includes(procedure.procedure_id);
                    return (
                      <label key={procedure.procedure_id} className={`flex min-h-14 cursor-pointer items-start gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0 ${checked ? "bg-sky-50" : "hover:bg-slate-50"}`}>
                        <input data-testid={`supply-procedure-${procedure.procedure_id}`} type="checkbox" checked={checked} onChange={() => toggleProcedure(procedure.procedure_id)} className="mt-0.5 h-4 w-4 rounded border-slate-300" />
                        <span className="min-w-0"><span className="block text-sm font-semibold text-slate-900">{procedure.name}</span><span className="mt-0.5 block text-xs text-slate-500">{procedure.category}{procedure.code ? ` · ${procedure.code}` : ""}</span></span>
                      </label>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs font-semibold text-slate-600">{form.procedureIds.length} {form.procedureIds.length === 1 ? "treatment" : "treatments"} selected</p>
              </div> : null}
              {form.countingMethod === "exact" && (
                <Field label={`Usual ${quantityUnit(form.stockUnit || "unit", 2)} used per ${form.usageBasis === "patient_visit" ? "patient visit" : "selected treatment"}`}>
                  <input data-testid="supply-units-per-use" type="number" min="0.01" step="0.01" value={form.unitsPerUse} onChange={(event) => update("unitsPerUse", event.target.value)} className="field-input text-right" />
                  <span className="mt-1.5 block text-xs font-normal leading-5 text-slate-500">{form.usageBasis === "patient_visit" ? `This amount is recorded once when a patient visit at ${branchName} is completed.` : "This number applies to all selected treatments. You can change one treatment later under Treatment materials."}</span>
                </Field>
              )}
              {form.usageBasis === "treatment" ? <Field label="When should stock be reduced?">
                <select data-testid="supply-usage-scope" value={form.usageScope} onChange={(event) => update("usageScope", event.target.value)} className="field-input bg-white">
                  <option value="completed_treatment">Once, when the treatment is completed</option>
                  <option value="patient_visit">After every appointment for the treatment</option>
                </select>
                <span className="mt-1.5 block text-xs font-normal leading-5 text-slate-500">A treatment can take several appointments; choose every appointment only if this supply is used each time the patient returns.</span>
              </Field> : null}
            </div>
            <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-950">
              {form.countingMethod === "estimate" ? (
                <>
                  <p className="font-semibold">1 {stockUnitName(form.stockUnit)} → about {formatQuantity(form.usesPerStockUnit)} {form.usageBasis === "patient_visit" ? "patient visits" : form.usageScope === "patient_visit" ? "treatment appointments" : "completed treatments"}</p>
                  {form.usageBasis === "treatment" ? <p>Applies to {form.procedureIds.length} selected {form.procedureIds.length === 1 ? "treatment" : "treatments"}.</p> : null}
                  <p>{formatQuantity(form.openingQuantity)} {quantityUnit(stockUnitName(form.stockUnit), form.openingQuantity)} on hand → about {formatQuantity(numberValue(form.openingQuantity) * numberValue(form.usesPerStockUnit))} {form.usageBasis === "patient_visit" ? "patient visits" : "treatments"}</p>
                  <p className="mt-1 text-xs text-emerald-800">{form.usageBasis === "patient_visit" ? "The standard amount is recorded once when each patient visit is completed." : form.usageScope === "patient_visit" ? "Stock decreases after every appointment for a selected treatment." : "Stock decreases once when a selected treatment is completed."}</p>
                </>
              ) : form.usageBasis === "patient_visit"
                ? `Dental OS counts ${formatQuantity(form.unitsPerUse)} ${quantityUnit(form.stockUnit, form.unitsPerUse)} once when each patient visit is completed.`
                : form.usageScope === "patient_visit"
                ? `Dental OS counts ${formatQuantity(form.unitsPerUse)} ${quantityUnit(form.stockUnit, form.unitsPerUse)} after every appointment for a selected treatment.`
                : `Dental OS counts ${formatQuantity(form.unitsPerUse)} ${quantityUnit(form.stockUnit, form.unitsPerUse)} once when a selected treatment is completed.`}
            </div>
            <p className="mt-3 text-xs text-slate-500">Starting stock: {formatQuantity(form.openingQuantity)} · Low-stock alert at {formatQuantity(form.reorderPoint)}</p>
          </div>
        )}

        {formError && <p role="alert" className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{formError}</p>}
      </div>

      <footer className="flex flex-col-reverse gap-2 border-t border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <button type="button" onClick={() => step === 1 ? onClose() : setStep((current) => current - 1)} className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto">{step === 1 ? "Cancel" : "Back"}</button>
        <button type="button" disabled={saving} onClick={continueFlow} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-5 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-wait disabled:opacity-60 sm:w-auto">
          {saving && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {step === 3 ? "Start tracking" : "Continue"}
        </button>
      </footer>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return <label className={`block text-sm font-semibold text-slate-700 ${className}`}>{label}{children}</label>;
}

function StockUnitControl({ value, onChange, testId, customTestId }) {
  const normalized = String(value || "").trim().toLowerCase();
  const knownOption = STOCK_UNIT_OPTIONS.find((option) => option.value === normalized);
  const [custom, setCustom] = useState(Boolean(value) && !knownOption);

  useEffect(() => {
    if (knownOption) setCustom(false);
  }, [knownOption]);

  function selectUnit(nextValue) {
    if (nextValue === "__custom__") {
      setCustom(true);
      onChange("");
      return;
    }
    setCustom(false);
    onChange(nextValue);
  }

  return (
    <div className="mt-2">
      <select data-testid={testId} aria-label="Counted by" value={custom ? "__custom__" : knownOption?.value || ""} onChange={(event) => selectUnit(event.target.value)} className="field-input bg-white">
        <option value="">Choose packaging</option>
        {STOCK_UNIT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        <option value="__custom__">Other (create your own)</option>
      </select>
      {custom ? <input data-testid={customTestId} aria-label="Custom counted-by unit" autoFocus value={value} onChange={(event) => onChange(event.target.value)} placeholder="e.g. tray, sachet, spool" className="field-input mt-2" /> : null}
    </div>
  );
}

function InventoryFirstRun({ branchName, onStart, readOnly = false }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white px-5 py-12 text-center sm:px-8" data-testid="inventory-first-run">
      <span className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600">
        <PackageOpen className="h-5 w-5" aria-hidden="true" />
      </span>
      <h2 className="mt-4 text-lg font-bold text-slate-950">No supplies yet</h2>
      <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">{readOnly ? `No supplies have been added for ${branchName} yet.` : `Add a supply to start tracking stock at ${branchName}.`}</p>
      {!readOnly ? (
        <button type="button" onClick={onStart} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-slate-950 px-5 text-sm font-semibold text-white hover:bg-slate-800">
          <Plus className="h-4 w-4" aria-hidden="true" />Add supply
        </button>
      ) : null}
    </section>
  );
}

function StockLevels({ supplies, branchName, onSnapshot, onAddSupply, readOnly = false }) {
  const [view, setView] = useState("all");
  const [search, setSearch] = useState("");
  const [drafts, setDrafts] = useState({});
  const [reason, setReason] = useState("Shelf count");
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => setDrafts({}), [supplies]);

  const counts = useMemo(() => ({
    all: supplies.length,
    low: supplies.filter((item) => item.status === "low" || item.status === "out").length,
  }), [supplies]);

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return supplies.filter((item) => {
      const matchesView = view === "all" || item.status === "low" || item.status === "out";
      const searchValues = [item.name, item.sku, item.category, ...(item.used_in || [])];
      return matchesView && (!query || searchValues.some((value) => String(value || "").toLowerCase().includes(query)));
    });
  }, [search, supplies, view]);

  const pending = useMemo(() => readOnly ? [] : supplies.filter((item) => {
    const value = drafts[item.branch_item_id];
    return value !== undefined && value !== "" && numberValue(value) !== numberValue(item.on_hand);
  }), [drafts, readOnly, supplies]);

  async function saveChanges() {
    if (readOnly) return;
    setSaving(true);
    let latest = null;
    for (const item of pending) {
      const next = numberValue(drafts[item.branch_item_id]);
      if (reason === "Stock received" && next < numberValue(item.on_hand)) {
        toast.error(`${item.name}: received stock cannot lower the count.`);
        setSaving(false);
        return;
      }
      if (reason === "Damaged or expired" && next > numberValue(item.on_hand)) {
        toast.error(`${item.name}: damaged stock cannot raise the count.`);
        setSaving(false);
        return;
      }
      const { data, error } = await supabase.rpc("inventory_admin_set_quantity", {
        p_branch_item_id: item.branch_item_id,
        p_counted_quantity: next,
        p_reason: reason,
        p_request_id: newRequestId(),
      });
      if (error) {
        toast.error(errorMessage(error, `Could not save ${item.name}.`));
        setSaving(false);
        return;
      }
      latest = data;
    }
    setSaving(false);
    if (latest) onSnapshot(latest);
    setDrafts({});
    toast.success(`${pending.length} stock ${pending.length === 1 ? "count" : "counts"} saved`);
  }

  if (supplies.length === 0) {
    return (
      <section className="rounded-xl border border-dashed border-slate-300 bg-white px-6 py-16 text-center" data-testid="inventory-empty-state">
        <PackageOpen className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
        <h2 className="mt-4 text-lg font-bold text-slate-950">{readOnly ? "No supplies yet" : "Add your first supply"}</h2>
        <p className="mt-2 text-sm text-slate-500">{readOnly ? `A Clinic Admin has not added supplies for ${branchName} yet.` : "Start with one frequently used item. You do not need to enter the entire stockroom today."}</p>
        {!readOnly ? <button type="button" onClick={onAddSupply} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white"><Plus className="h-4 w-4" />Add first supply</button> : null}
      </section>
    );
  }

  return (
    <>
      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex min-h-12 items-end gap-1 overflow-x-auto border-b border-slate-200 px-3 pt-2">
          {[{ id: "all", label: "All inventory" }, { id: "low", label: "Low stock" }].map((option) => (
            <button key={option.id} type="button" onClick={() => setView(option.id)} className={`flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-semibold ${view === option.id ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500"}`}>
              {option.label}<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{counts[option.id]}</span>
            </button>
          ))}
        </div>
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="relative max-w-lg">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search supply, SKU, category, or treatment" aria-label="Search inventory" className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-base outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100 sm:text-sm" />
          </div>
        </div>
        <div className="overflow-visible sm:overflow-x-auto">
          <table className="block w-full border-collapse text-left sm:table sm:min-w-[650px] lg:min-w-[920px] xl:min-w-[1220px]" data-testid="inventory-table">
            <thead className="hidden sm:table-header-group"><tr className="border-b border-slate-200 text-xs font-semibold text-slate-500">
              <th className="min-w-[210px] px-4 py-3 sm:sticky sm:left-0 sm:z-[2] sm:bg-white">Supply</th><th className="hidden w-36 px-3 py-3 xl:table-cell">SKU</th><th className="hidden w-36 px-3 py-3 xl:table-cell">Category</th><th className="hidden min-w-[180px] px-3 py-3 lg:table-cell">Used in</th><th className="w-32 px-3 py-3">On hand</th><th className="w-20 px-2 py-3">Reorder at</th><th className="hidden w-28 px-3 py-3 lg:table-cell">30-day use</th><th className="hidden w-32 px-3 py-3 xl:table-cell">Projection</th><th className="w-24 px-2 py-3">Status</th><th className="hidden w-36 px-3 py-3 xl:table-cell">Last count</th>{!readOnly ? <th className="w-24 px-3 py-3 text-right">Action</th> : null}
            </tr></thead>
            <tbody className="block divide-y divide-slate-100 sm:table-row-group">
              {visible.map((item) => {
                const value = drafts[item.branch_item_id] ?? String(item.on_hand);
                const changed = value !== "" && numberValue(value) !== numberValue(item.on_hand);
                const status = STATUS_STYLES[item.status] || STATUS_STYLES.in_stock;
                const expanded = expandedId === item.branch_item_id;
                return (
                  <Fragment key={item.branch_item_id}>
                  <tr className={`block px-4 py-4 text-sm hover:bg-slate-50/80 sm:table-row sm:px-0 sm:py-0 ${expanded ? "bg-slate-50" : ""}`}>
                    <td className="block p-0 sm:sticky sm:left-0 sm:z-[1] sm:table-cell sm:bg-white sm:px-4 sm:py-3.5"><div className="flex items-start justify-between gap-3 sm:block"><div><p className="font-semibold text-slate-950">{item.name}</p><p className="mt-0.5 text-xs text-slate-500">per {item.stock_unit} · {item.counting_method === "estimate" ? `about ${formatQuantity(item.uses_per_stock_unit)} ${item.in_visit_kit ? "patient visits" : "treatments"} each` : item.counting_method === "manual" ? "manual count" : "exact count"}</p></div><span className={`inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold sm:hidden ${status.classes}`}>{status.label}</span></div></td>
                    <td className="hidden px-3 py-3.5 font-mono text-xs text-slate-500 xl:table-cell">{item.sku || "—"}</td>
                    <td className="hidden px-3 py-3.5 text-slate-700 xl:table-cell">{item.category}</td>
                    <td className="mt-3 block p-0 text-sky-700 sm:hidden lg:table-cell lg:px-3 lg:py-3.5"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:hidden">Used in</span>{item.used_in?.length ? `${item.used_in[0]}${item.used_in.length > 1 ? ` +${item.used_in.length - 1}` : ""}` : "Not linked"}</td>
                    <td className="mt-4 block p-0 sm:table-cell sm:px-3 sm:py-2.5"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:hidden">On hand</span>{readOnly ? <div className="flex items-baseline gap-1.5"><span className="font-bold tabular-nums text-slate-950">{formatQuantity(item.on_hand)}</span><span className="text-xs text-slate-500">{item.stock_unit}</span></div> : <><div className="flex items-center gap-2"><input type="number" min="0" step="0.01" value={value} onChange={(event) => setDrafts((current) => ({ ...current, [item.branch_item_id]: event.target.value }))} aria-label={`${item.name} on hand`} className={`h-11 w-24 rounded-md border px-2 text-right text-base font-semibold tabular-nums outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100 sm:w-20 sm:text-sm ${changed ? "border-sky-500 bg-sky-50" : "border-slate-300"}`} /><span className="text-xs text-slate-500">{item.stock_unit}</span></div>{changed && <p className="mt-1 text-xs font-medium text-sky-700">{formatQuantity(item.on_hand)} → {formatQuantity(value)}</p>}</>}</td>
                    <td className="mt-4 inline-block w-1/2 p-0 align-top font-medium tabular-nums text-slate-700 sm:table-cell sm:w-auto sm:px-2 sm:py-3.5"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:hidden">Reorder at</span>{formatQuantity(item.reorder_point)}</td>
                    <td className="mt-4 inline-block w-1/2 p-0 align-top tabular-nums text-slate-700 sm:hidden lg:table-cell lg:w-auto lg:px-3 lg:py-3.5"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:hidden">30-day use</span>{formatQuantity(item.used_30_days)}</td>
                    <td className="hidden px-3 py-3.5 text-xs text-slate-600 xl:table-cell">{item.estimated_days_remaining === null ? "Not enough data" : `About ${formatQuantity(item.estimated_days_remaining)} days`}</td>
                    <td className="hidden px-2 py-3.5 sm:table-cell"><span className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${status.classes}`}>{status.label}</span></td>
                    <td className="hidden px-3 py-3.5 text-xs text-slate-500 xl:table-cell">{formatDate(item.last_counted_at)}</td>
                    {!readOnly ? <td className="mt-4 block p-0 sm:table-cell sm:px-3 sm:py-3.5 sm:text-right"><button type="button" onClick={() => setExpandedId(expanded ? null : item.branch_item_id)} aria-expanded={expanded} className="inline-flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 sm:w-auto"><PencilLine className="h-3.5 w-3.5" aria-hidden="true" />Manage</button></td> : null}
                  </tr>
                  {!readOnly && expanded ? <tr className="block sm:table-row" data-testid={`supply-manager-${item.branch_item_id}`}><td colSpan="11" className="block bg-slate-50 px-3 py-4 sm:table-cell sm:px-4"><SupplyManager item={item} onClose={() => setExpandedId(null)} onSnapshot={onSnapshot} /></td></tr> : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {visible.length === 0 && <div className="px-6 py-14 text-center text-sm text-slate-500">No supplies match this view.</div>}
        <footer className="flex flex-col gap-1 border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 sm:flex-row sm:justify-between"><span>Showing {visible.length} of {supplies.length} supplies</span><span>Quantities are scoped to {branchName}</span></footer>
      </section>

      {!readOnly && pending.length > 0 && (
        <div className="sticky bottom-4 z-20 flex flex-col gap-3 rounded-xl border border-slate-300 bg-white px-4 py-3 shadow-[0_12px_30px_rgba(15,23,42,0.16)] sm:flex-row sm:items-center sm:justify-between" data-testid="pending-changes">
          <div className="flex items-center gap-3"><span className="flex h-8 w-8 items-center justify-center rounded-full bg-sky-100 text-sky-700"><Check className="h-4 w-4" /></span><div><p className="text-sm font-semibold text-slate-950">{pending.length} unsaved stock {pending.length === 1 ? "count" : "counts"}</p><p className="text-xs text-slate-500">The database records the difference and keeps the history.</p></div></div>
          <div className="grid grid-cols-2 items-center gap-2 sm:flex sm:flex-wrap">
            <label className="col-span-2 flex min-h-11 w-full items-center justify-between gap-2 rounded-lg border border-slate-300 px-3 text-sm text-slate-600 sm:w-auto"><span>Reason</span><select value={reason} onChange={(event) => setReason(event.target.value)} className="min-w-0 flex-1 bg-transparent font-semibold text-slate-900 outline-none"><option>Shelf count</option><option>Stock received</option><option>Damaged or expired</option><option>Correction</option></select><ChevronDown className="h-4 w-4 text-slate-400" /></label>
            <button type="button" onClick={() => setDrafts({})} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700">Discard</button>
            <button type="button" disabled={saving} onClick={saveChanges} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-60">{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save changes</button>
          </div>
        </div>
      )}
    </>
  );
}

function SupplyManager({ item, onClose, onSnapshot }) {
  const [form, setForm] = useState(() => ({
    name: item.name || "",
    sku: item.sku || "",
    category: item.category || "",
    stockUnit: item.stock_unit || "",
    reorderPoint: String(item.reorder_point ?? 0),
  }));
  const [saving, setSaving] = useState(false);
  const [confirmStop, setConfirmStop] = useState(false);
  const [formError, setFormError] = useState("");

  function update(key, value) {
    setForm((current) => ({ ...current, [key]: value }));
    setFormError("");
  }

  async function persist(active) {
    if (!form.name.trim() || !form.category.trim() || !form.stockUnit.trim()) {
      setFormError("Supply name, category, and counted-by unit are required.");
      return;
    }
    if (numberValue(form.reorderPoint, -1) < 0) {
      setFormError("Low-stock alert must be zero or more.");
      return;
    }

    setSaving(true);
    setFormError("");
    const { data, error } = await supabase.rpc("inventory_admin_update_supply", {
      p_branch_item_id: item.branch_item_id,
      p_name: form.name.trim(),
      p_sku: form.sku.trim() || null,
      p_category: form.category.trim(),
      p_stock_unit: form.stockUnit.trim(),
      p_counting_method: item.counting_method,
      p_uses_per_stock_unit: numberValue(item.uses_per_stock_unit, 1),
      p_reorder_point: numberValue(form.reorderPoint),
      p_active: active,
    });
    setSaving(false);
    if (error) {
      setFormError(errorMessage(error, active ? "Could not save the supply." : "Could not stop tracking the supply."));
      return;
    }

    onSnapshot(data || EMPTY_SNAPSHOT);
    onClose();
    toast.success(active ? "Supply details saved" : `${item.name} is no longer tracked`);
  }

  const countingLabel = item.counting_method === "estimate"
    ? `One ${item.stock_unit} lasts about ${formatQuantity(item.uses_per_stock_unit)} ${item.in_visit_kit ? "patient visits" : "treatments"}`
    : item.counting_method === "manual"
      ? "Counted during stocktake"
      : `Exact amount, counted by ${item.stock_unit}`;

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 lg:ml-10" aria-label={`Manage ${item.name}`}>
      <div className="flex flex-col gap-2 border-b border-slate-200 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div><h3 className="text-sm font-bold text-slate-950">Supply details</h3><p className="mt-1 text-xs text-slate-500">Correct how this supply appears on the stock sheet.</p></div>
        <button type="button" onClick={onClose} className="min-h-11 self-start rounded-md px-3 text-xs font-semibold text-slate-500 hover:bg-slate-100">Close</button>
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Field label="Supply name" className="sm:col-span-2"><input data-testid="edit-supply-name" value={form.name} onChange={(event) => update("name", event.target.value)} className="field-input" /></Field>
        <div>
          <p className="text-sm font-semibold text-slate-700">Counted by</p>
          <StockUnitControl value={form.stockUnit} onChange={(value) => update("stockUnit", value)} testId="edit-supply-stock-unit" customTestId="edit-supply-custom-stock-unit" />
        </div>
        <Field label="Low-stock alert"><input data-testid="edit-supply-reorder-point" type="number" min="0" step="0.01" value={form.reorderPoint} onChange={(event) => update("reorderPoint", event.target.value)} className="field-input text-right" /></Field>
        <Field label="SKU (optional)"><input data-testid="edit-supply-sku" value={form.sku} onChange={(event) => update("sku", event.target.value)} className="field-input" /></Field>
        <Field label="Category"><input data-testid="edit-supply-category" value={form.category} onChange={(event) => update("category", event.target.value)} className="field-input" /></Field>
        <div className="sm:col-span-2"><p className="text-sm font-semibold text-slate-700">Usage</p><div className="mt-2 min-h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-700">{countingLabel}</div><p className="mt-1.5 text-xs text-slate-500">{item.in_visit_kit ? "Change the standard amount under Patient visit kit." : "Change treatment amounts under Treatment materials."}</p></div>
      </div>
      {formError ? <p role="alert" className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700">{formError}</p> : null}
      <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
        {confirmStop ? (
          <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:flex-wrap sm:items-center" data-testid="stop-tracking-confirmation"><p className="mr-1 text-sm text-slate-700">Hide this supply? Its stock history will be kept.</p><button type="button" disabled={saving} onClick={() => persist(false)} className="min-h-11 rounded-lg border border-rose-300 bg-rose-50 px-3 text-sm font-semibold text-rose-800">Yes, stop tracking</button><button type="button" disabled={saving} onClick={() => setConfirmStop(false)} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700">Cancel</button></div>
        ) : <button type="button" onClick={() => setConfirmStop(true)} className="min-h-11 self-start px-1 text-sm font-semibold text-rose-700 hover:text-rose-800">Stop tracking</button>}
        <button type="button" disabled={saving} onClick={() => persist(true)} className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-60 sm:w-auto">{saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}Save details</button>
      </div>
    </section>
  );
}

function visitKitUsageSentence(rule) {
  if (rule.counting_method === "estimate") {
    const visits = numberValue(rule.uses_per_stock_unit, 1) / numberValue(rule.units_per_visit, 1);
    return `1 ${rule.stock_unit} lasts about ${formatQuantity(visits)} patient visits`;
  }
  return `${formatQuantity(rule.units_per_visit)} ${quantityUnit(rule.stock_unit, rule.units_per_visit)} per patient visit`;
}

function PatientVisitKit({ rules, supplies, branchId, branchName, onSnapshot, onAddSupply }) {
  const linkable = supplies.filter((supply) => (
    supply.counting_method !== "manual"
    && !rules.some((rule) => rule.supply_id === supply.supply_id)
  ));
  const [supplyId, setSupplyId] = useState(linkable[0]?.supply_id || "");
  const [unitsPerVisit, setUnitsPerVisit] = useState("1");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    if (!linkable.some((supply) => supply.supply_id === supplyId)) {
      setSupplyId(linkable[0]?.supply_id || "");
    }
  }, [linkable, supplyId]);

  async function addRule() {
    if (!supplyId || numberValue(unitsPerVisit) <= 0) {
      setFormError("Choose a supply and enter an amount greater than zero.");
      return;
    }
    setSaving(true);
    setFormError("");
    const { data, error } = await supabase.rpc("inventory_admin_upsert_visit_kit_rule", {
      p_branch_id: branchId,
      p_rule_id: null,
      p_supply_id: supplyId,
      p_units_per_visit: numberValue(unitsPerVisit, 1),
      p_active: true,
    });
    setSaving(false);
    if (error) {
      setFormError(errorMessage(error, "Could not add the supply to the patient visit kit."));
      return;
    }
    onSnapshot(data || EMPTY_SNAPSHOT);
    setUnitsPerVisit("1");
    toast.success("Patient visit kit updated");
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="patient-visit-kit">
      <header className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2"><CalendarDays className="h-5 w-5 text-slate-600" aria-hidden="true" /><h2 className="text-lg font-bold text-slate-950">Standard patient visit kit</h2></div>
          <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">Gloves, masks, bibs, and other standard supplies used regardless of treatment. Dental OS records this kit once when a patient visit at {branchName} is completed.</p>
        </div>
        <button type="button" onClick={onAddSupply} className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700"><Plus className="h-4 w-4" aria-hidden="true" />Add new supply</button>
      </header>

      {rules.length === 0 ? (
        <div className="px-5 py-12 text-center">
          <PackageOpen className="mx-auto h-7 w-7 text-slate-400" aria-hidden="true" />
          <p className="mt-3 text-sm font-semibold text-slate-800">No standard visit supplies yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm leading-6 text-slate-500">Add gloves or another supply below. Treatment-specific materials stay under Treatment materials.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {rules.map((rule) => <PatientVisitKitRow key={rule.rule_id} rule={rule} branchId={branchId} onSnapshot={onSnapshot} />)}
        </div>
      )}

      <div className="border-t border-slate-200 bg-slate-50 px-4 py-4">
        {linkable.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-[minmax(220px,1fr)_180px_auto] sm:items-end">
            <Field label="Add an existing supply">
              <select data-testid="visit-kit-supply" value={supplyId} onChange={(event) => { setSupplyId(event.target.value); setFormError(""); }} className="field-input bg-white">
                {linkable.map((supply) => <option key={supply.supply_id} value={supply.supply_id}>{supply.name}</option>)}
              </select>
            </Field>
            <Field label="Standard uses per visit"><input data-testid="visit-kit-units" type="number" min="0.01" step="0.01" value={unitsPerVisit} onChange={(event) => { setUnitsPerVisit(event.target.value); setFormError(""); }} className="field-input text-right" /></Field>
            <button type="button" disabled={saving} onClick={addRule} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}Add to kit</button>
          </div>
        ) : supplies.length === 0 ? (
          <p className="text-sm text-slate-600">Add an exact or estimated supply before building the standard patient visit kit.</p>
        ) : (
          <p className="text-sm text-slate-600">Every eligible supply at this branch is already in the standard kit.</p>
        )}
        {formError ? <p role="alert" className="mt-3 text-sm text-rose-700">{formError}</p> : null}
        <p className="mt-3 text-xs leading-5 text-slate-500">This is automatic recording, not a reorder workflow. Low stock remains visible but never blocks patient care or checkout.</p>
      </div>
    </section>
  );
}

function PatientVisitKitRow({ rule, branchId, onSnapshot }) {
  const [editing, setEditing] = useState(false);
  const [unitsPerVisit, setUnitsPerVisit] = useState(String(rule.units_per_visit ?? 1));
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  async function persist(active) {
    if (numberValue(unitsPerVisit) <= 0) {
      setFormError("Enter an amount greater than zero.");
      return;
    }
    setSaving(true);
    setFormError("");
    const { data, error } = await supabase.rpc("inventory_admin_upsert_visit_kit_rule", {
      p_branch_id: branchId,
      p_rule_id: rule.rule_id,
      p_supply_id: rule.supply_id,
      p_units_per_visit: numberValue(unitsPerVisit, 1),
      p_active: active,
    });
    setSaving(false);
    if (error) {
      setFormError(errorMessage(error, active ? "Could not update the patient visit kit." : "Could not remove the supply from the patient visit kit."));
      return;
    }
    onSnapshot(data || EMPTY_SNAPSHOT);
    setEditing(false);
    toast.success(active ? "Patient visit amount saved" : `${rule.name} removed from the patient visit kit`);
  }

  return (
    <article className="grid gap-3 px-4 py-4 sm:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)_auto] sm:items-center">
      <div><p className="text-sm font-semibold text-slate-950">{rule.name}</p><p className="mt-0.5 text-xs text-slate-500">Counted by {rule.stock_unit}</p></div>
      {editing ? (
        <div><label className="text-xs font-semibold text-slate-600">Standard uses per visit<input data-testid={`visit-kit-rule-${rule.rule_id}-units`} type="number" min="0.01" step="0.01" value={unitsPerVisit} onChange={(event) => { setUnitsPerVisit(event.target.value); setFormError(""); }} className="field-input max-w-40 text-right" /></label>{formError ? <p role="alert" className="mt-2 text-xs text-rose-700">{formError}</p> : null}</div>
      ) : <p className="text-sm text-slate-700">{visitKitUsageSentence(rule)}</p>}
      <div className="flex gap-2 sm:justify-end">
        {editing ? <><button type="button" disabled={saving} onClick={() => { setEditing(false); setUnitsPerVisit(String(rule.units_per_visit ?? 1)); setFormError(""); }} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700">Cancel</button><button type="button" disabled={saving} onClick={() => persist(true)} className="min-h-11 rounded-lg bg-slate-950 px-3.5 text-sm font-semibold text-white disabled:opacity-60">Save</button></> : <><button type="button" onClick={() => setEditing(true)} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700">Change</button><button type="button" disabled={saving} onClick={() => persist(false)} aria-label={`Remove ${rule.name} from patient visit kit`} className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-300 bg-white text-slate-500 hover:border-rose-300 hover:text-rose-700"><Trash2 className="h-4 w-4" aria-hidden="true" /></button></>}
      </div>
    </article>
  );
}

function ProcedureMaterials({ procedures, supplies, branchId, onSnapshot, onAddSupply }) {
  const tracked = procedures.filter((procedure) => procedure.status === "ready").length;
  const untracked = procedures.length - tracked;
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(tracked > 0 ? "tracked" : "all");
  const [expandedId, setExpandedId] = useState(null);
  const visible = procedures.filter((procedure) => {
    const query = search.trim().toLowerCase();
    const matchesFilter = filter === "all"
      || (filter === "tracked" && procedure.status === "ready")
      || (filter === "untracked" && procedure.status !== "ready");
    return matchesFilter && (!query || [procedure.name, procedure.code, procedure.category].some((value) => String(value || "").toLowerCase().includes(query)));
  });

  if (supplies.length === 0) {
    return (
      <section className="rounded-xl border border-slate-200 bg-white px-6 py-14 text-center shadow-sm" data-testid="materials-prerequisite">
        <PackageOpen className="mx-auto h-8 w-8 text-slate-400" aria-hidden="true" />
        <h2 className="mt-4 text-lg font-bold text-slate-950">Add supplies first</h2>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-600">Build the stock list before connecting supplies to treatments. You only need to connect treatments whose materials you want Dental OS to count.</p>
        <button type="button" onClick={onAddSupply} className="mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white"><Plus className="h-4 w-4" />Add first supply</button>
      </section>
    );
  }

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="procedure-materials">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between"><div><div className="flex items-center gap-2"><Settings2 className="h-5 w-5 text-slate-600" /><h2 className="text-lg font-bold text-slate-950">Treatment materials</h2></div><p className="mt-1 text-sm text-slate-500">Add supplies first, then connect only the treatments you want Dental OS to count.</p></div><button type="button" onClick={onAddSupply} className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700"><Plus className="h-4 w-4" />Add supply</button></div>
      <div className="flex min-h-12 items-end gap-1 overflow-x-auto border-b border-slate-200 px-3 pt-2">
        {[{ id: "all", label: "All", count: procedures.length }, { id: "tracked", label: "Tracking", count: tracked }, { id: "untracked", label: "Not tracked", count: untracked }].map((option) => <button key={option.id} type="button" onClick={() => setFilter(option.id)} className={`flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-3 text-sm font-semibold ${filter === option.id ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500"}`}>{option.label}<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs">{option.count}</span></button>)}
      </div>
      <div className="border-b border-slate-200 bg-slate-50 px-4 py-3"><div className="relative max-w-lg"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input type="search" value={search} onChange={(event) => setSearch(event.target.value)} aria-label="Search procedure materials" placeholder="Search procedure, category, or code" className="h-11 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-base outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100 sm:text-sm" /></div></div>
      <div className="overflow-visible sm:overflow-x-auto"><table className="block w-full border-collapse text-left sm:table sm:min-w-[650px] lg:min-w-[900px]" data-testid="procedure-materials-table"><thead className="hidden sm:table-header-group"><tr className="border-b border-slate-200 text-xs font-semibold text-slate-500"><th className="min-w-[220px] px-4 py-3">Procedure</th><th className="min-w-[230px] px-3 py-3">Materials normally used</th><th className="hidden w-44 px-3 py-3 lg:table-cell">Stock changes</th><th className="w-24 px-2 py-3">Status</th><th className="w-32 px-3 py-3 text-right">Action</th></tr></thead><tbody className="block divide-y divide-slate-100 sm:table-row-group">
        {visible.map((procedure) => <ProcedureRow key={procedure.procedure_id} procedure={procedure} supplies={supplies} branchId={branchId} expanded={expandedId === procedure.procedure_id} onToggle={() => setExpandedId(expandedId === procedure.procedure_id ? null : procedure.procedure_id)} onSnapshot={onSnapshot} />)}
      </tbody></table></div>
      {visible.length === 0 && <div className="px-6 py-14 text-center text-sm text-slate-500">No procedures match this view.</div>}
      <footer className="flex flex-col gap-1 border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500 sm:flex-row sm:justify-between"><span>{tracked} {tracked === 1 ? "treatment" : "treatments"} connected</span><span>Unconnected treatments do not change stock</span></footer>
    </section>
  );
}

function ProcedureRow({ procedure, supplies, branchId, expanded, onToggle, onSnapshot }) {
  const materials = procedure.materials || [];
  const mapped = materials.length > 0;
  const methods = [...new Set(materials.map((item) => item.usage_scope))];
  const countedBy = !mapped ? "—" : methods.length > 1 ? "Mixed" : methods[0] === "patient_visit" ? "After every appointment" : "When treatment is completed";

  return (
    <>
      <tr className={`block px-4 py-4 text-sm hover:bg-slate-50 sm:table-row sm:px-0 sm:py-0 ${expanded ? "bg-slate-50" : ""}`}>
        <td className="block p-0 sm:table-cell sm:px-4 sm:py-3.5"><div className="flex items-start gap-2.5"><button type="button" onClick={onToggle} aria-label={`${expanded ? "Collapse" : "Edit"} materials for ${procedure.name}`} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-slate-500 hover:bg-slate-200 sm:h-9 sm:w-9"><ChevronRight className={`h-4 w-4 transition-transform ${expanded ? "rotate-90" : ""}`} /></button><div className="min-w-0 flex-1"><div className="flex items-start justify-between gap-2"><p className="font-semibold text-slate-950">{procedure.name}</p><span className={`inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold sm:hidden ${mapped ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>{mapped ? "Tracking" : "Not tracked"}</span></div><p className="mt-0.5 font-mono text-xs text-slate-500">{procedure.code || "No code"} · {procedure.category}</p></div></div></td>
        <td className={`mt-3 block p-0 sm:table-cell sm:px-3 sm:py-3.5 ${mapped ? "text-slate-700" : "text-slate-500"}`}><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 sm:hidden">Materials normally used</span>{mapped ? `${materials[0].supply_name}${materials.length > 1 ? ` +${materials.length - 1} more` : ""}` : "Not tracked"}</td>
        <td className="mt-3 block p-0 text-slate-600 sm:hidden lg:table-cell lg:px-3 lg:py-3.5"><span className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500 lg:hidden">Stock changes</span>{countedBy}</td>
        <td className="hidden px-3 py-3.5 sm:table-cell"><span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${mapped ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "border-slate-200 bg-slate-50 text-slate-600"}`}>{mapped ? "Tracking" : "Not tracked"}</span></td>
        <td className="mt-4 block p-0 sm:table-cell sm:px-4 sm:py-3.5 sm:text-right"><button type="button" onClick={onToggle} className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 sm:w-auto">{mapped ? "Edit materials" : "Add materials"}</button></td>
      </tr>
      {expanded && <tr className="block sm:table-row" data-testid={`recipe-editor-${procedure.procedure_id}`}><td colSpan="5" className="block bg-slate-50 px-3 py-4 sm:table-cell sm:px-4"><RecipeEditor procedure={procedure} materials={materials} supplies={supplies} branchId={branchId} onSnapshot={onSnapshot} /></td></tr>}
    </>
  );
}

function RecipeEditor({ procedure, materials, supplies, branchId, onSnapshot }) {
  const linkable = supplies.filter((supply) => supply.counting_method !== "manual");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState({ supplyId: linkable[0]?.supply_id || "", unitsPerUse: "1", usageScope: "completed_treatment", visitSequence: "", applyToCategory: false });

  function beginEdit(material) {
    setEditing(material);
    setForm({ supplyId: material.supply_id, unitsPerUse: String(material.units_per_use), usageScope: material.usage_scope, visitSequence: material.visit_sequence ? String(material.visit_sequence) : "", applyToCategory: material.rule_source === "category" });
    setFormError("");
  }

  function resetForm() {
    setEditing(null);
    setForm({ supplyId: linkable[0]?.supply_id || "", unitsPerUse: "1", usageScope: "completed_treatment", visitSequence: "", applyToCategory: false });
    setFormError("");
  }

  async function saveRule(active = true, material = null) {
    const target = material || editing;
    const payload = material ? {
      supplyId: material.supply_id,
      unitsPerUse: material.units_per_use,
      usageScope: material.usage_scope,
      visitSequence: material.visit_sequence,
      applyToCategory: material.rule_source === "category",
    } : form;
    if (!payload.supplyId || numberValue(payload.unitsPerUse) <= 0) {
      setFormError("Choose a supply and enter how much is normally used.");
      return;
    }
    setSaving(true);
    setFormError("");
    const { data, error } = await supabase.rpc("inventory_admin_upsert_rule", {
      p_branch_id: branchId,
      p_rule_id: target?.rule_id || null,
      p_scope: payload.applyToCategory ? "category" : "procedure",
      p_procedure_id: payload.applyToCategory ? null : procedure.procedure_id,
      p_procedure_category: payload.applyToCategory ? procedure.category : null,
      p_supply_id: payload.supplyId,
      p_units_per_use: numberValue(payload.unitsPerUse, 1),
      p_usage_scope: payload.usageScope,
      p_visit_sequence: payload.visitSequence ? numberValue(payload.visitSequence) : null,
      p_active: active,
    });
    setSaving(false);
    if (error) {
      setFormError(errorMessage(error, "Could not save the material rule."));
      return;
    }
    onSnapshot(data);
    resetForm();
    toast.success(active ? "Procedure materials saved" : "Material removed");
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3 sm:p-4 lg:ml-10">
      <div className="flex flex-col gap-2 border-b border-slate-200 pb-3 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm font-bold text-slate-950">Materials normally used</p><p className="mt-1 text-xs text-slate-500">Stock changes only after completed checkout work is recorded.</p></div><span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700"><Link2 className="h-3.5 w-3.5" />Linked to Services catalog</span></div>
      <div className="mt-3 divide-y divide-slate-100 rounded-lg border border-slate-200">
        {materials.length === 0 && <div className="px-3.5 py-4 text-sm text-slate-500">No supplies linked yet.</div>}
        {materials.map((material) => <div key={material.rule_id} className="flex flex-col gap-2 px-3.5 py-3 sm:flex-row sm:items-center sm:justify-between"><div><p className="text-sm font-semibold text-slate-900">{material.supply_name}</p><p className="mt-0.5 text-xs text-slate-500">{usageSentence(material)}{material.visit_sequence ? ` · Appointment ${material.visit_sequence}` : ""}{material.rule_source === "category" ? ` · ${procedure.category} category default` : ""}</p></div><div className="flex gap-2"><button type="button" onClick={() => beginEdit(material)} className="min-h-11 flex-1 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 sm:flex-none">Change</button><button type="button" disabled={saving} onClick={() => saveRule(false, material)} aria-label={`Remove ${material.supply_name}`} className="flex h-11 w-11 items-center justify-center rounded-lg border border-slate-300 text-slate-500 hover:text-rose-700"><Trash2 className="h-4 w-4" /></button></div></div>)}
      </div>

      {linkable.length === 0 ? (
        <p className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 text-sm text-amber-800">Add an exact or estimated supply before linking procedure materials.</p>
      ) : (
        <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3.5">
          <p className="text-sm font-bold text-slate-900">{editing ? "Change material" : "Add material"}</p>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Supply" className="sm:col-span-2"><select value={form.supplyId} onChange={(event) => setForm((current) => ({ ...current, supplyId: event.target.value }))} className="field-input bg-white">{linkable.map((supply) => <option key={supply.supply_id} value={supply.supply_id} label={supply.name} />)}</select></Field>
            <Field label="Amount used"><input type="number" min="0.01" step="0.01" value={form.unitsPerUse} onChange={(event) => setForm((current) => ({ ...current, unitsPerUse: event.target.value }))} className="field-input text-right" /></Field>
            <Field label="Appointment number (optional)"><input type="number" min="1" value={form.visitSequence} onChange={(event) => setForm((current) => ({ ...current, visitSequence: event.target.value }))} className="field-input text-right" /></Field>
            <Field label="When should stock be reduced?" className="sm:col-span-2"><select data-testid="recipe-usage-scope" value={form.usageScope} onChange={(event) => setForm((current) => ({ ...current, usageScope: event.target.value }))} className="field-input bg-white"><option value="completed_treatment">Once, when the treatment is completed</option><option value="patient_visit">After every appointment for the treatment</option></select></Field>
            <label className="flex min-h-11 items-center gap-2 text-sm font-semibold text-slate-700 sm:col-span-2 lg:pt-7"><input type="checkbox" checked={form.applyToCategory} onChange={(event) => setForm((current) => ({ ...current, applyToCategory: event.target.checked }))} className="h-5 w-5 rounded" />Use as the {procedure.category} category default</label>
          </div>
          {form.applyToCategory && <p className="mt-3 text-xs text-sky-800">This material will appear on every {procedure.category} procedure unless that procedure has its own rule.</p>}
          {formError && <p role="alert" className="mt-3 text-sm text-rose-700">{formError}</p>}
          <div className="mt-3 grid grid-cols-2 gap-2 sm:flex sm:justify-end">{editing && <button type="button" onClick={resetForm} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700">Cancel</button>}<button type="button" disabled={saving} onClick={() => saveRule(true)} className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white disabled:opacity-60 ${editing ? "" : "col-span-2"}`}>{saving && <Loader2 className="h-4 w-4 animate-spin" />}Save material</button></div>
        </div>
      )}
    </div>
  );
}

function InventoryHistory({ supplies, branchName, readOnly = false }) {
  const [selectedDate, setSelectedDate] = useState(() => clinicDateKey());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadHistory = useCallback(async () => {
    if (supplies.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    const results = await Promise.all(supplies.map(async (supply) => {
      const { data, error: rpcError } = await supabase.rpc(readOnly ? "inventory_role_movement_history" : "inventory_admin_movement_history", {
        p_branch_item_id: supply.branch_item_id,
        p_limit: 200,
      });
      if (rpcError) throw rpcError;
      return (Array.isArray(data?.movements) ? data.movements : []).map((movement) => ({
        ...movement,
        supply_id: supply.supply_id,
        supply_name: supply.name,
        stock_unit: supply.stock_unit,
      }));
    })).catch((historyError) => {
      setError(errorMessage(historyError, "Stock history could not be loaded."));
      return null;
    });

    if (results) {
      setRows(results.flat().sort((a, b) => new Date(b.occurred_at) - new Date(a.occurred_at)));
    }
    setLoading(false);
  }, [readOnly, supplies]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const dailyRows = useMemo(
    () => rows.filter((row) => clinicDateKey(row.occurred_at) === selectedDate),
    [rows, selectedDate],
  );
  const dailySummary = useMemo(() => ({
    patientVisits: new Set(dailyRows.filter((row) => row.movement_kind === "visit_usage").map((row) => row.metadata?.visit_id).filter(Boolean)).size,
    treatmentRecords: dailyRows.filter((row) => row.movement_kind === "treatment_usage").length,
    suppliesUsed: new Set(dailyRows.filter((row) => ["treatment_usage", "visit_usage"].includes(row.movement_kind)).map((row) => row.supply_id)).size,
    deliveries: dailyRows.filter((row) => row.movement_kind === "receipt").length,
    otherChanges: dailyRows.filter((row) => !["treatment_usage", "visit_usage", "receipt"].includes(row.movement_kind)).length,
  }), [dailyRows]);

  return (
    <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="inventory-stock-history">
      <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-slate-600" aria-hidden="true" />
            <h2 className="text-lg font-bold text-slate-950">Daily stock report</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">Recent stock changes at {branchName}, grouped by clinic day.</p>
        </div>
        <div className="flex flex-col gap-2 min-[420px]:flex-row">
          <label className="text-xs font-semibold text-slate-600">
            Report date
            <input type="date" value={selectedDate} onChange={(event) => setSelectedDate(event.target.value)} aria-label="Inventory report date" className="mt-1 block h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-800 outline-none focus:border-sky-600 focus:ring-2 focus:ring-sky-100" />
          </label>
          <button type="button" onClick={() => setSelectedDate(clinicDateKey())} className="min-h-11 self-end rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700 hover:bg-slate-50">Today</button>
        </div>
      </div>

      <div className="border-b border-slate-200 bg-slate-50 px-4 py-4">
        <p className="text-sm font-bold text-slate-950">{formatReportDate(selectedDate)}</p>
        <dl className="mt-3 flex flex-wrap gap-x-6 gap-y-3 text-sm">
          <div><dt className="text-xs text-slate-500">Patient visits</dt><dd className="mt-0.5 font-bold tabular-nums text-slate-950">{dailySummary.patientVisits}</dd></div>
          <div><dt className="text-xs text-slate-500">Treatment records</dt><dd className="mt-0.5 font-bold tabular-nums text-slate-950">{dailySummary.treatmentRecords}</dd></div>
          <div><dt className="text-xs text-slate-500">Supplies used</dt><dd className="mt-0.5 font-bold tabular-nums text-slate-950">{dailySummary.suppliesUsed}</dd></div>
          <div><dt className="text-xs text-slate-500">Deliveries</dt><dd className="mt-0.5 font-bold tabular-nums text-slate-950">{dailySummary.deliveries}</dd></div>
          <div><dt className="text-xs text-slate-500">Other stock changes</dt><dd className="mt-0.5 font-bold tabular-nums text-slate-950">{dailySummary.otherChanges}</dd></div>
        </dl>
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 px-5 py-16 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" />Loading stock history</div>
      ) : error ? (
        <div className="px-5 py-12 text-center"><p className="text-sm text-rose-700">{error}</p><button type="button" onClick={loadHistory} className="mt-4 min-h-11 rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700">Try again</button></div>
      ) : dailyRows.length === 0 ? (
        <div className="px-5 py-14 text-center">
          <CalendarDays className="mx-auto h-7 w-7 text-slate-400" aria-hidden="true" />
          <p className="mt-3 text-sm font-semibold text-slate-800">No stock changes recorded for this day</p>
          <p className="mt-1 text-sm text-slate-500">Patient visit kits, treatment use, deliveries, and shelf counts will appear here.</p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {dailyRows.map((row) => {
            const delta = numberValue(row.quantity_delta);
            const sessionRecorded = row.metadata?.source === "checkout_session";
            return (
              <article key={row.movement_id} className="grid gap-2 px-4 py-3.5 text-sm sm:grid-cols-[88px_minmax(180px,1fr)_minmax(180px,1fr)_110px_110px] sm:items-center">
                <time className="text-xs font-medium text-slate-500">{formatMovementTime(row.occurred_at)}</time>
                <div><p className="font-semibold text-slate-950">{row.supply_name}</p><p className="mt-0.5 text-xs text-slate-500">{movementLabel(row.movement_kind)}</p></div>
                <div><p className="text-slate-700">{sessionRecorded ? row.reason : (row.procedure_name || row.reason)}</p>{!sessionRecorded && row.procedure_name && row.reason ? <p className="mt-0.5 text-xs text-slate-500">{row.reason}</p> : null}</div>
                <div className={`font-bold tabular-nums ${delta < 0 ? "text-rose-700" : "text-emerald-700"}`}><span className="mr-1 text-xs font-medium text-slate-500 sm:hidden">Change</span>{delta > 0 ? "+" : ""}{formatQuantity(delta)} {row.stock_unit}</div>
                <div className="tabular-nums text-slate-700"><span className="mr-1 text-xs font-medium text-slate-500 sm:hidden">Balance</span>{formatQuantity(row.balance_after)} {row.stock_unit}</div>
              </article>
            );
          })}
        </div>
      )}

      <footer className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">Showing up to 200 recent changes per supply. History is read-only; corrections are recorded as a new stock change.</footer>
    </section>
  );
}

export default function InventoryPreview() {
  const { clinicRole } = useAuth();
  const { activeBranch, activeBranchId, loading: branchLoading } = useBranch();
  const isAdmin = clinicRole === "clinic_admin";
  const [snapshot, setSnapshot] = useState(EMPTY_SNAPSHOT);
  const [workspace, setWorkspace] = useState("stock");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [setupOpen, setSetupOpen] = useState(() => (
    isAdmin && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("setup") === "first"
  ));
  const branchName = activeBranch?.name?.trim() || snapshot.branch_name || "Current branch";

  const loadInventory = useCallback(async () => {
    if (!activeBranchId) return;
    setLoading(true);
    setError("");
    const { data, error: rpcError } = await supabase.rpc(isAdmin ? "inventory_admin_snapshot_v2" : "inventory_role_snapshot", { p_branch_id: activeBranchId });
    setLoading(false);
    if (rpcError) {
      setError(errorMessage(rpcError, "Inventory could not be loaded."));
      return;
    }
    setSnapshot(data || EMPTY_SNAPSHOT);
  }, [activeBranchId, isAdmin]);

  useEffect(() => { loadInventory(); }, [loadInventory]);

  useEffect(() => {
    if (isAdmin) return;
    setSetupOpen(false);
    setWorkspace((current) => ["kit", "materials"].includes(current) ? "stock" : current);
  }, [isAdmin]);

  function closeSetup() {
    setSetupOpen(false);
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("setup")) return;
    url.searchParams.delete("setup");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }

  if (branchLoading || (loading && !snapshot.generated_at)) return <PageState icon={Loader2} title="Loading inventory" body="Reading stock levels from the local database." />;
  if (!activeBranchId) return <PageState icon={PackageOpen} title="Choose an active branch" body="Inventory quantities belong to one branch. Select a branch before opening Inventory." />;
  if (error) return <PageState icon={ShieldAlert} title="Inventory could not be loaded" body={error} action={<button type="button" onClick={loadInventory} className="mt-5 inline-flex min-h-10 items-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white"><RefreshCw className="h-4 w-4" />Try again</button>} />;

  const supplies = Array.isArray(snapshot.supplies) ? snapshot.supplies : [];
  const procedures = Array.isArray(snapshot.procedures) ? snapshot.procedures : [];
  const visitKit = Array.isArray(snapshot.visit_kit) ? snapshot.visit_kit : [];
  const refreshSnapshot = () => loadInventory();
  return (
    <main className="mx-auto max-w-[1320px] space-y-4 p-3 text-slate-950 sm:p-5 lg:p-6" data-testid="inventory-page">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div><h1 className="text-[30px] font-bold tracking-[-0.025em] text-slate-950">Inventory</h1><p className="mt-1 text-sm text-slate-500">{!isAdmin ? `View current stock and recorded changes at ${branchName}.` : setupOpen ? `Add a supply to ${branchName} without doing inventory math.` : workspace === "stock" ? `Start here: add and count supplies kept at ${branchName}.` : workspace === "kit" ? `Set the standard supplies recorded once for every completed patient visit at ${branchName}.` : workspace === "materials" ? "Optional: connect treatment-specific supplies to completed work." : `Review daily stock activity recorded at ${branchName}.`}</p></div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap">
          <button type="button" disabled={supplies.length === 0} onClick={() => exportInventoryCsv(supplies, branchName)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3.5 text-sm font-semibold text-slate-700 shadow-sm disabled:opacity-50"><ArrowDownToLine className="h-4 w-4" />Export CSV</button>
          {isAdmin ? <button type="button" onClick={() => setSetupOpen(true)} className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white shadow-sm"><Plus className="h-4 w-4" />Add supply</button> : null}
        </div>
      </header>

      {!isAdmin ? <div className="rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900" data-testid="inventory-view-only"><span className="font-semibold">View only.</span> A Clinic Admin manages supplies, stock counts, and treatment materials.</div> : null}

      {isAdmin && setupOpen ? (
        <GuidedSupplySetup branchId={activeBranchId} branchName={branchName} procedures={procedures} onClose={closeSetup} onSaved={refreshSnapshot} />
      ) : (
        <>
          <nav className="-mx-3 flex items-center gap-1 overflow-x-auto border-b border-slate-200 px-3 sm:mx-0 sm:px-0" aria-label="Inventory workspace">
            <button type="button" onClick={() => setWorkspace("stock")} className={`min-h-11 shrink-0 border-b-2 px-4 text-sm font-bold ${workspace === "stock" ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500"}`}>Supplies</button>
            {isAdmin ? <button type="button" onClick={() => setWorkspace("kit")} className={`min-h-11 shrink-0 border-b-2 px-4 text-sm font-bold ${workspace === "kit" ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500"}`}>Patient visit kit</button> : null}
            {isAdmin ? <button type="button" onClick={() => setWorkspace("materials")} className={`flex min-h-11 shrink-0 items-center gap-2 border-b-2 px-4 text-sm font-bold ${workspace === "materials" ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500"}`}>Treatment materials<span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-500">Optional</span></button> : null}
            <button type="button" onClick={() => setWorkspace("history")} className={`min-h-11 shrink-0 border-b-2 px-4 text-sm font-bold ${workspace === "history" ? "border-slate-950 text-slate-950" : "border-transparent text-slate-500"}`}>Stock history</button>
          </nav>
          {workspace === "stock" ? (
            supplies.length === 0
              ? <InventoryFirstRun branchName={branchName} onStart={() => setSetupOpen(true)} readOnly={!isAdmin} />
              : <StockLevels supplies={supplies} branchName={branchName} onSnapshot={refreshSnapshot} onAddSupply={() => setSetupOpen(true)} readOnly={!isAdmin} />
          ) : workspace === "kit" ? (
            <PatientVisitKit rules={visitKit} supplies={supplies} branchId={activeBranchId} branchName={branchName} onSnapshot={refreshSnapshot} onAddSupply={() => setSetupOpen(true)} />
          ) : workspace === "materials" ? (
            <ProcedureMaterials procedures={procedures} supplies={supplies} branchId={activeBranchId} onSnapshot={refreshSnapshot} onAddSupply={() => { setWorkspace("stock"); setSetupOpen(true); }} />
          ) : (
            <InventoryHistory supplies={supplies} branchName={branchName} readOnly={!isAdmin} />
          )}
        </>
      )}

      <style>{`.field-input{display:block;width:100%;height:2.75rem;margin-top:.5rem;border:1px solid #cbd5e1;border-radius:.5rem;padding:0 .75rem;font-size:.875rem;color:#0f172a;outline:none}.field-input:focus{border-color:#0284c7;box-shadow:0 0 0 3px #e0f2fe}`}</style>
    </main>
  );
}
